// import React, { useMemo } from "react";
// import Link from "next/link";
// import Head from "next/head";
// import { useRouter } from "next/router";

// const Breadcrumbs = ({
//   items,
//   labelMap = {},
//   dynamicLabel,
//   hideSegments = [],
//   className = "",
//   separator = ">",
//   showJsonLd = true,
//   rtl = false,
// }) => {
//   const router = useRouter();
//   const locale = router.locale;

//   const { crumbs, jsonLd } = useMemo(() => {
//     if (Array.isArray(items) && items.length) {
//       const normalized = items.map((it, idx) => ({
//         label: it.label,
//         href: it.href,
//         current: it.current ?? idx === items.length - 1,
//       }));

//       return {
//         crumbs: normalized,
//         jsonLd: buildJsonLdFromItems(normalized),
//       };
//     }

//     const asPath = (router.asPath || "/").split("?")[0].split("#")[0];

//     const path = stripLocalePrefix(asPath, locale);

//     const segments = path
//       .split("/")
//       .filter(Boolean)
//       .filter((seg) => !hideSegments.includes(seg));

//     const auto = [];
//     // Home
//     auto.push({ label: labelMap.home || "Home", href: "/" });

//     let acc = "";
//     segments.forEach((seg, idx) => {
//       acc += `/${seg}`;

//       const isLast = idx === segments.length - 1;

//       // Label: map -> prettify
//       let label = labelMap[seg] || prettify(seg);

//       // If last segment is dynamic, let caller override it
//       if (isLast && dynamicLabel) label = dynamicLabel;

//       auto.push({
//         label,
//         href: isLast ? undefined : acc, // last crumb not clickable
//         current: isLast,
//       });
//     });

//     return {
//       crumbs: auto,
//       jsonLd: buildJsonLdFromItems(auto),
//     };
//   }, [items, router.asPath, locale, labelMap, dynamicLabel, hideSegments]);

//   return (
//     <>
//       {showJsonLd && jsonLd && (
//         <Head>
//           <script
//             type="application/ld+json"
//             dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
//           />
//         </Head>
//       )}

//       <nav
//         aria-label="Breadcrumb"
//         className={`w-full text-[14px] sm:text-[15px] ${className}`}
//       >
//         <ol
//           dir={locale === "ar" ? "rtl" : "ltr"}
//           className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-white/90 p-0 ${
//             locale === "ar" ? "justify-center" : ""
//           }`}
//         >
//           {crumbs.map((c, idx) => {
//             const isLast = idx === crumbs.length - 1;

//             return (
//               <li key={`${c.label}-${idx}`} className="flex items-center">
//                 {c.href && !isLast ? (
//                   <Link
//                     href={withLocale(c.href, locale)}
//                     className="hover:text-white transition-colors"
//                   >
//                     {c.label}
//                   </Link>
//                 ) : (
//                   <span
//                     aria-current={isLast ? "page" : undefined}
//                     className={isLast ? "text-white font-medium" : ""}
//                   >
//                     {c.label}
//                   </span>
//                 )}

//                 {!isLast && (
//                   <span
//                     className={`text-white/60 ${rtl ? "ml-2 mr-0" : "mx-2"}`}
//                   >
//                     {separator}
//                   </span>
//                 )}
//               </li>
//             );
//           })}
//         </ol>
//       </nav>
//     </>
//   );
// };

// export default Breadcrumbs;

// /** helpers */

// function prettify(seg) {
//   // decode, replace - and _ with space, Title Case
//   const s = decodeURIComponent(seg).replace(/[-_]+/g, " ").trim();
//   return s.charAt(0).toUpperCase() + s.slice(1);
// }

// function stripLocalePrefix(path, locale) {
//   // Handles /ar/..., /en/...
//   if (!locale) return path;
//   const prefix = `/${locale}`;
//   if (path === prefix) return "/";
//   if (path.startsWith(prefix + "/")) return path.slice(prefix.length);
//   return path;
// }

// function withLocale(href, locale) {
//   // If you are using Next i18n routing, Next will usually handle it,
//   // but this makes it explicit/safe for direct links.
//   if (!locale) return href;
//   if (href === "/") return `/${locale}`;
//   // Avoid double prefix
//   if (href.startsWith(`/${locale}/`) || href === `/${locale}`) return href;
//   return `/${locale}${href}`;
// }

// function buildJsonLdFromItems(crumbs) {
//   // Best effort: only include crumbs that have a URL (and include current as well if desired)
//   // We'll include all crumbs, using href or empty for current.
//   const itemListElement = crumbs.map((c, idx) => ({
//     "@type": "ListItem",
//     position: idx + 1,
//     name: c.label,
//     item: c.href || undefined,
//   }));

//   return {
//     "@context": "https://schema.org",
//     "@type": "BreadcrumbList",
//     itemListElement,
//   };
// }

import React, { useMemo } from "react";
import Link from "next/link";
import Head from "next/head";
import { useRouter } from "next/router";

const SITE_URL = "https://www.prism-me.com";

const Breadcrumbs = ({
  items,
  labelMap = {},
  dynamicLabel,
  hideSegments = [],
  className = "",
  separator = ">",
  showJsonLd = true,
}) => {
  const router = useRouter();
  const locale = router.locale;
  const isRTL = locale === "ar";

  const { crumbs, jsonLd } = useMemo(() => {
    if (Array.isArray(items) && items.length) {
      const normalized = items.map((it, idx) => ({
        label: it.label,
        href: it.href,
        current: it.current ?? idx === items.length - 1,
      }));

      return {
        crumbs: normalized,
        jsonLd: buildJsonLdFromItems(normalized, locale, router.asPath),
      };
    }

    const asPath = (router.asPath || "/").split("?")[0].split("#")[0];
    const path = stripLocalePrefix(asPath);

    const segments = path
      .split("/")
      .filter(Boolean)
      .filter((seg) => !hideSegments.includes(seg));

    const auto = [{ label: labelMap.home || "Home", href: "/" }];

    let acc = "";
    segments.forEach((seg, idx) => {
      acc += `/${seg}`;
      const isLast = idx === segments.length - 1;

      let label = labelMap[seg] || prettify(seg);
      if (isLast && dynamicLabel) label = dynamicLabel;

      auto.push({
        label,
        href: isLast ? undefined : acc,
        current: isLast,
      });
    });

    return {
      crumbs: auto,
      jsonLd: buildJsonLdFromItems(auto, locale, router.asPath),
    };
  }, [items, router.asPath, locale, labelMap, dynamicLabel, hideSegments]);

  return (
    <>
      {showJsonLd && jsonLd && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
          />
        </Head>
      )}

      <nav
        aria-label="Breadcrumb"
        className={`w-full text-[14px] sm:text-[15px] ${className}`}
      >
        <ol
          dir={isRTL ? "rtl" : "ltr"}
          className={`flex flex-wrap items-center gap-x-2 gap-y-1 p-0 text-white/90 ${
            isRTL ? "justify-center" : ""
          }`}
        >
          {crumbs.map((c, idx) => {
            const isLast = idx === crumbs.length - 1;

            return (
              <li key={`${c.label}-${idx}`} className="flex items-center">
                {c.href && !isLast ? (
                  <Link
                    href={withLocale(c.href, locale)}
                    className="transition-colors hover:text-white"
                  >
                    {c.label}
                  </Link>
                ) : (
                  <span
                    aria-current={isLast ? "page" : undefined}
                    className={isLast ? "font-medium text-white" : ""}
                  >
                    {c.label}
                  </span>
                )}

                {!isLast && (
                  <span className="mx-2 text-white/60">{separator}</span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
};

export default Breadcrumbs;

/** helpers */

function prettify(seg) {
  const s = decodeURIComponent(seg).replace(/[-_]+/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function stripLocalePrefix(path) {
  if (path === "/ar") return "/";
  if (path.startsWith("/ar/")) return path.slice(3);
  if (path === "/en") return "/";
  if (path.startsWith("/en/")) return path.slice(3);
  return path;
}

function withLocale(href, locale) {
  if (!href.startsWith("/")) href = `/${href}`;

  const cleaned = href.replace(/^\/(en|ar)(?=\/|$)/, "") || "/";

  if (locale === "ar") {
    return cleaned === "/" ? "/ar" : `/ar${cleaned}`;
  }

  return cleaned;
}

function toAbsoluteUrl(path) {
  return `${SITE_URL}${path === "/" ? "" : path}`;
}

function buildJsonLdFromItems(crumbs, locale, asPath = "/") {
  const cleanAsPath = (asPath || "/").split("?")[0].split("#")[0];
  const currentPath = withLocale(stripLocalePrefix(cleanAsPath), locale);

  const itemListElement = crumbs.map((c, idx) => {
    const isLast = idx === crumbs.length - 1;
    const path = isLast ? currentPath : withLocale(c.href || "/", locale);

    return {
      "@type": "ListItem",
      position: idx + 1,
      name: c.label,
      item: toAbsoluteUrl(path),
    };
  });

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement,
  };
}
